<template>
  <!-- asPanel: rendered in place inside AdminSidebar.vue instead of as a popup -->
  <Teleport to="body" :disabled="asPanel">
    <div :class="asPanel ? 'contents' : 'fixed inset-0 bg-black/50 flex items-start justify-center overflow-y-auto py-10 px-4 z-50'" @click.self="!asPanel && close()">
      <div class="relative bg-surface dark:bg-surface-dark rounded-xl w-full" :class="asPanel ? 'border border-stroke dark:border-stroke-dark' : 'shadow-2xl max-w-2xl'">

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-stroke dark:border-stroke-dark">
          <div>
            <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">New Membership Offer</h3>
            <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">Creates a membership record and sends the signup link to the contact.</p>
          </div>
          <button @click="close" class="p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <form @submit.prevent="submit" class="px-6 py-5 space-y-5">

          <!-- Contact + Rate + Start Date + Notes -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label :class="labelClass">Contact *</label>
              <Combobox v-model="selectedContact" nullable>
                <div class="relative">
                  <ComboboxInput
                    :class="inputClass + ' pr-8'"
                    :displayValue="c => c?.name ?? ''"
                    @change="contactQuery = $event.target.value"
                    placeholder="Type to search contacts…"
                    autocomplete="off"
                  />
                  <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon class="h-4 w-4 text-fg-faint" />
                  </ComboboxButton>
                  <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" @after-leave="contactQuery = ''">
                    <ComboboxOptions :class="optionsClass + ' max-h-60'">
                      <div v-if="filteredContacts.length === 0" class="px-4 py-3 text-fg-muted dark:text-fg-muted-dark">
                        No contacts match "{{ contactQuery }}"
                      </div>
                      <ComboboxOption v-for="c in filteredContacts" :key="c.id" :value="c" v-slot="{ active, selected }">
                        <li :class="['flex flex-col px-3 py-2 cursor-pointer select-none', active ? 'bg-blue-600 text-white' : 'text-fg-base dark:text-fg-base-dark']">
                          <span :class="['font-medium truncate', selected ? 'font-semibold' : '']">{{ c.name }}</span>
                          <span v-if="c.email" :class="['text-xs truncate', active ? 'text-blue-200' : 'text-fg-faint']">{{ c.email }}</span>
                        </li>
                      </ComboboxOption>
                    </ComboboxOptions>
                  </TransitionRoot>
                </div>
              </Combobox>
              <input type="text" :value="selectedContact?.id ?? ''" required class="sr-only" tabindex="-1" aria-hidden="true" />
            </div>

            <div>
              <label :class="labelClass">Monthly Rate (USD) *</label>
              <input v-model="form.monthly_rate_dollars" type="number" min="1" step="0.01" required placeholder="e.g. 349" :class="inputClass" />
            </div>

            <div>
              <label :class="labelClass">Start Date <span class="font-normal text-fg-faint">{{ defaultStartDate ? '(default: earliest unpaid lesson)' : '(optional)' }}</span></label>
              <input v-model="form.start_date" type="date" :class="inputClass" @input="startDateTouched = true" />
            </div>

            <div>
              <label :class="labelClass">Notes (optional)</label>
              <input v-model="form.notes" placeholder="Any internal notes…" :class="inputClass" />
            </div>

            <div class="sm:col-span-2">
              <label :class="labelClass">Lessons</label>
              <div class="flex flex-wrap items-center gap-2">
                <select v-model="form.frequency" :class="inputClass + ' sm:w-56'">
                  <option value="weekly">Every week</option>
                  <option value="weeks">Specific weeks of the month</option>
                </select>
                <template v-if="form.frequency === 'weeks'">
                  <button v-for="w in WEEKS" :key="w.n" type="button" @click="toggleWeek(w.n)"
                    :class="['px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors',
                      form.weeks.includes(w.n) ? 'bg-blue-600 border-blue-600 text-white' : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark']">
                    {{ w.label }}
                  </button>
                </template>
              </div>
              <p v-if="form.frequency === 'weeks'" class="text-xs text-fg-faint mt-1">
                {{ form.weeks.length }} lesson{{ form.weeks.length === 1 ? '' : 's' }} per month per student, on those weeks' occurrence of each student's day.
                Lessons that already happened off-pattern still move onto the membership when the family accepts.
              </p>
            </div>
          </div>

          <!-- Student schedule blocks -->
          <div class="space-y-4">
            <div v-for="(s, idx) in form.students" :key="idx" class="border border-stroke dark:border-stroke-dark rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <span class="text-xs font-semibold text-fg-secondary dark:text-fg-secondary-dark uppercase tracking-wide">
                  Student {{ form.students.length > 1 ? idx + 1 : '' }} Schedule
                </span>
                <button v-if="form.students.length > 1" type="button" @click="form.students.splice(idx, 1)"
                  class="text-xs text-red-500 hover:text-red-600 dark:text-red-400">Remove</button>
              </div>
              <div v-if="sourceSeries.length" class="mb-3">
                <label :class="labelClass">Replaces current schedule</label>
                <select v-model="s.source_series_id" @change="applySource(s)" :class="inputClass">
                  <option :value="null">— none (new schedule) —</option>
                  <option v-for="src in sourceSeries" :key="src.series_id" :value="src.series_id"
                    :disabled="form.students.some(o => o !== s && o.source_series_id === src.series_id)">
                    {{ sourceLabel(src) }}
                  </option>
                </select>
                <p v-if="s.source_series_id" class="text-xs text-fg-faint mt-1">
                  Schedule is locked to this series. When the family accepts, its unpaid lessons from the start date on move onto the membership.
                </p>
              </div>
              <fieldset :disabled="!!s.source_series_id" class="grid grid-cols-1 sm:grid-cols-2 gap-3 disabled:opacity-60">
                <div>
                  <label :class="labelClass">Student (optional)</label>
                  <select v-model="s.student_id" :disabled="!selectedContact" :class="inputClass + ' disabled:opacity-40'">
                    <option value="">— select student —</option>
                    <option v-for="st in studentsForContact" :key="st.id" :value="st.id">{{ st.name }}</option>
                  </select>
                </div>

                <div>
                  <label :class="labelClass">Teacher *</label>
                  <Combobox v-model="s.teacher" nullable>
                    <div class="relative">
                      <ComboboxInput
                        :class="inputClass + ' pr-8'"
                        :displayValue="t => t?.name ?? ''"
                        @change="s.teacherQuery = $event.target.value"
                        placeholder="Type to search teachers…"
                        autocomplete="off"
                      />
                      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon class="h-4 w-4 text-fg-faint" />
                      </ComboboxButton>
                      <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <ComboboxOptions :class="optionsClass + ' max-h-48'">
                          <div v-if="filteredTeachers(s).length === 0" class="px-4 py-3 text-fg-muted dark:text-fg-muted-dark">No teachers match</div>
                          <ComboboxOption v-for="t in filteredTeachers(s)" :key="t.id" :value="t" v-slot="{ active, selected }">
                            <li :class="['px-3 py-2 cursor-pointer select-none', active ? 'bg-blue-600 text-white' : 'text-fg-base dark:text-fg-base-dark']">
                              <span :class="['font-medium', selected ? 'font-semibold' : '']">{{ t.name }}</span>
                            </li>
                          </ComboboxOption>
                        </ComboboxOptions>
                      </TransitionRoot>
                    </div>
                  </Combobox>
                </div>

                <div>
                  <label :class="labelClass">Instrument *</label>
                  <input v-model="s.instrument" required placeholder="e.g. Piano" :class="inputClass" />
                </div>

                <div>
                  <label :class="labelClass">Day of Week *</label>
                  <select v-model="s.day_of_week" required :class="inputClass">
                    <option value="">— select day —</option>
                    <option v-for="(d, i) in DAYS" :key="i" :value="i">{{ d }}</option>
                  </select>
                </div>

                <div>
                  <label :class="labelClass">Time *</label>
                  <input v-model="s.start_time" required placeholder="e.g. 16:00" pattern="\d{1,2}:\d{2}" :class="inputClass" />
                </div>

                <div>
                  <label :class="labelClass">Duration</label>
                  <select v-model="s.duration_minutes" :class="inputClass">
                    <option :value="30">30 min</option>
                    <option :value="45">45 min</option>
                    <option :value="60">60 min</option>
                  </select>
                </div>

                <div class="sm:col-span-2">
                  <label :class="labelClass">Location <span class="font-normal text-fg-faint">(optional)</span></label>
                  <input v-model="s.location" placeholder="e.g. Studio B, Zoom, 123 Main St" :class="inputClass" />
                </div>
              </fieldset>
            </div>

            <button type="button" @click="form.students.push(blankStudent())"
              class="text-xs text-blue-600 dark:text-blue-400 hover:underline">
              + Add another student
            </button>
          </div>

          <p v-if="error" class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>

          <!-- Actions -->
          <div class="flex justify-end gap-2 pt-3 border-t border-stroke dark:border-stroke-dark">
            <button type="button" @click="close"
              class="px-4 py-2 text-sm font-medium rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark transition-colors">
              Cancel
            </button>
            <button type="submit" :disabled="submitting"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {{ submitting ? 'Creating…' : 'Create & Send Invite' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { XMarkIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'
import {
  Combobox, ComboboxInput, ComboboxButton, ComboboxOptions,
  ComboboxOption, TransitionRoot,
} from '@headlessui/vue'

const props = defineProps({
  // An existing membership (as returned by GET /memberships) to prefill from — the
  // Memberships page's "Clone" action. Omitted for a blank offer.
  cloneFrom: { type: Object, default: null },
  // Render inside the admin sidebar (AdminSidebar.vue) rather than as a popup.
  asPanel: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'saved'])

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const WEEKS = [{ n: 1, label: '1st' }, { n: 2, label: '2nd' }, { n: 3, label: '3rd' }, { n: 4, label: '4th' }, { n: -1, label: 'Last' }]
const labelClass   = 'block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1'
const inputClass   = 'w-full px-3 py-2 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:ring-2 focus:ring-blue-500 focus:border-transparent'
const optionsClass = 'absolute z-20 mt-1 w-full overflow-auto rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg text-sm focus:outline-none'

const contacts   = ref([])
const teachers   = ref([])
const submitting = ref(false)
const error      = ref('')

const selectedContact = ref(null)
const contactQuery    = ref('')

function blankStudent() {
  return { source_series_id: null, student_id: '', teacher: null, teacherQuery: '', instrument: '', day_of_week: '', start_time: '', duration_minutes: 30, location: '' }
}

const form = ref({ monthly_rate_dollars: '', start_date: '', notes: '', frequency: 'weekly', weeks: [1, 2, 3], students: [blankStudent()] })

function toggleWeek(n) {
  const w = form.value.weeks
  form.value.weeks = w.includes(n) ? w.filter(x => x !== n) : [...w, n]
}

const filteredContacts = computed(() => {
  const q = contactQuery.value.toLowerCase().trim()
  if (!q) return contacts.value
  return contacts.value.filter(c =>
    c.name.toLowerCase().includes(q) || (c.email && c.email.toLowerCase().includes(q))
  )
})

const studentsForContact = computed(() => selectedContact.value?.students || [])

// The contact's active per-lesson series (GET /memberships/source-series) — a
// membership replaces one of these, and its schedule is copied from it.
const sourceSeries = ref([])
const startDateTouched = ref(false)

function sourceLabel(src) {
  const price = src.price_per_lesson_cents ? ` · $${(src.price_per_lesson_cents / 100).toFixed(0)}/lesson` : ''
  return `${src.student_name || 'Student'} — ${src.instrument || 'Lessons'} with ${src.teacher_name || '?'}, ${src.day_name || ''} ${src.start_time || ''}${price}`
}

function applySource(s) {
  const src = sourceSeries.value.find(x => x.series_id === s.source_series_id)
  if (!src) return
  s.student_id       = src.student_id ?? ''
  s.teacher          = teachers.value.find(t => t.id === src.teacher_row_id) || null
  s.instrument       = src.instrument || ''
  s.day_of_week      = src.day_of_week ?? ''
  s.start_time       = src.start_time || ''
  s.duration_minutes = src.duration_minutes || 30
  s.location         = src.location || ''
  if (!startDateTouched.value) form.value.start_date = defaultStartDate.value || ''
}

const defaultStartDate = computed(() => {
  const dates = form.value.students
    .map(s => sourceSeries.value.find(x => x.series_id === s.source_series_id)?.earliest_unpaid_date)
    .filter(Boolean)
    .sort()
  return dates[0] || null
})

async function loadSourceSeries(contact) {
  sourceSeries.value = []
  if (!contact) return
  try {
    sourceSeries.value = await getJson(`/api/memberships/source-series?contact_id=${contact.id}`)
  } catch { return }
  // One current schedule and a fresh offer: lock to it automatically.
  const first = form.value.students[0]
  if (!props.cloneFrom && sourceSeries.value.length === 1 && form.value.students.length === 1 && !first.source_series_id) {
    first.source_series_id = sourceSeries.value[0].series_id
    applySource(first)
  }
}

function filteredTeachers(s) {
  const q = (s.teacherQuery || '').toLowerCase().trim()
  if (!q) return teachers.value
  return teachers.value.filter(t => t.name.toLowerCase().includes(q))
}

// Changing the contact invalidates any student picks from the previous one. Skipped
// while prefilling from a clone, which sets contact and students together.
let prefilling = false
watch(selectedContact, c => {
  if (!prefilling) form.value.students.forEach(s => { s.student_id = ''; s.source_series_id = null })
  loadSourceSeries(c)
})

function applyClone(m) {
  prefilling = true
  selectedContact.value = contacts.value.find(c => c.id === m.contact_id) || null
  form.value = {
    monthly_rate_dollars: (m.monthly_rate_cents / 100).toFixed(0),
    start_date:           m.start_date || '',
    notes:                m.notes || '',
    frequency:            m.students?.[0]?.repeat_nths?.length ? 'weeks' : 'weekly',
    weeks:                m.students?.[0]?.repeat_nths || [1, 2, 3],
    students: (m.students?.length ? m.students : [null]).map(s => s ? {
      student_id:       s.student_id ?? '',
      teacher:          teachers.value.find(t => t.name === s.teacher_name) || null,
      teacherQuery:     '',
      instrument:       s.instrument || '',
      day_of_week:      s.day_of_week ?? '',
      start_time:       s.start_time || '',
      duration_minutes: s.duration_minutes || 30,
      location:         s.location || '',
    } : blankStudent()),
  }
  setTimeout(() => { prefilling = false })
}

async function getJson(url) {
  const r = await fetch(url, { credentials: 'include' })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function load() {
  const [c, t, settings] = await Promise.allSettled([
    getJson('/api/contacts?include_students=true'),
    getJson('/api/admin/teachers'),
    getJson('/api/admin/membership-settings'),
  ])
  if (c.status === 'fulfilled') contacts.value = c.value
  if (t.status === 'fulfilled') teachers.value = t.value
  if (props.cloneFrom) {
    applyClone(props.cloneFrom)
  } else if (settings.status === 'fulfilled') {
    const cents = settings.value.effective?.membership_standard_rate_cents
    if (cents != null && !form.value.monthly_rate_dollars) form.value.monthly_rate_dollars = (cents / 100).toFixed(0)
  }
}

async function submit() {
  if (!selectedContact.value) { error.value = 'Please select a contact.'; return }
  if (form.value.frequency === 'weeks' && !form.value.weeks.length) { error.value = 'Pick at least one week of the month.'; return }
  error.value = ''
  submitting.value = true
  try {
    const payload = {
      contact_id:         selectedContact.value.id,
      monthly_rate_cents: Math.round(parseFloat(form.value.monthly_rate_dollars) * 100),
      start_date:         form.value.start_date || null,
      notes:              form.value.notes.trim() || null,
      weeks_of_month:     form.value.frequency === 'weeks' ? form.value.weeks : null,
      students: form.value.students.map(s => ({
        source_series_id: s.source_series_id || null,
        student_id:       s.student_id ? Number(s.student_id) : null,
        teacher_id:       s.teacher?.id ?? null,
        instrument:       s.instrument.trim(),
        day_of_week:      Number(s.day_of_week),
        start_time:       s.start_time.trim(),
        duration_minutes: s.duration_minutes,
        repeat_interval:  'weekly',
        location:         s.location.trim() || null,
      })),
    }
    const r = await fetch('/api/memberships', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!r.ok) {
      const data = await r.json().catch(() => ({}))
      throw new Error(data.detail || `HTTP ${r.status}`)
    }
    const membership = await r.json()
    // Lets an open Memberships list refresh no matter which page opened this modal.
    window.dispatchEvent(new CustomEvent('memberships:changed', { detail: membership }))
    emit('saved', membership)
  } catch (e) {
    error.value = e.message || 'Failed to create membership.'
  } finally {
    submitting.value = false
  }
}

function close() { emit('close') }

// Esc closes, unless an open combobox already handled that Esc to close itself.
// In the sidebar, AdminSidebar.vue handles Esc (one level at a time).
const onKeydown = e => { if (e.key === 'Escape' && !e.defaultPrevented && !props.asPanel) close() }
onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  load()
})
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>
